import { useState } from 'react';
import {
  useReports,
  useReport,
  useGenerateReport,
  useDownloadReport,
  useDeleteReport,
  type ReportType,
  type ExportFormat,
  type GenerateReportParams,
} from '../api/reports';

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  { value: 'entity', label: 'Entity Report', description: 'Detailed profile and risk analysis of an entity' },
  { value: 'network', label: 'Network Report', description: 'Relationship network and connectivity analysis' },
  { value: 'timeline', label: 'Timeline Report', description: 'Chronological activity and event timeline' },
  { value: 'risk_assessment', label: 'Risk Assessment', description: 'Comprehensive risk scoring breakdown' },
  { value: 'investigation', label: 'Investigation Report', description: 'Full investigation findings and evidence' },
];

const FORMAT_OPTIONS: { value: ExportFormat; label: string; icon: string }[] = [
  { value: 'pdf', label: 'PDF', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
  { value: 'json', label: 'JSON', icon: 'M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5' },
  { value: 'csv', label: 'CSV', icon: 'M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 0v1.5c0 .621-.504 1.125-1.125 1.125' },
];

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  generating: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function ReportsPage() {
  const [showForm, setShowForm] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [formData, setFormData] = useState<GenerateReportParams>({
    title: '',
    type: 'entity',
    format: 'pdf',
    entity_ids: [],
    options: {},
  });
  const [entityIdInput, setEntityIdInput] = useState('');

  const { data: reportsData } = useReports({ page: 1, page_size: 50 });
  const { data: selectedReport } = useReport(selectedReportId);
  const generateReport = useGenerateReport();
  const downloadReport = useDownloadReport();
  const deleteReport = useDeleteReport();

  function handleGenerate() {
    if (!formData.title.trim()) return;
    generateReport.mutate(formData, {
      onSuccess: () => {
        setShowForm(false);
        setFormData({ title: '', type: 'entity', format: 'pdf', entity_ids: [], options: {} });
        setEntityIdInput('');
      },
    });
  }

  function handleAddEntityId() {
    if (entityIdInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        entity_ids: [...(prev.entity_ids || []), entityIdInput.trim()],
      }));
      setEntityIdInput('');
    }
  }

  function handleRemoveEntityId(id: string) {
    setFormData((prev) => ({
      ...prev,
      entity_ids: (prev.entity_ids || []).filter((eid) => eid !== id),
    }));
  }

  function formatFileSize(bytes?: number): string {
    if (!bytes) return '--';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Reports</h1>
          <p className="text-sm text-gray-400 mt-1">Generate, preview, and download investigation reports</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Report
        </button>
      </div>

      {/* Generate Form */}
      {showForm && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Generate New Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Report Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Enter report title..."
                className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Report Type</label>
              <div className="space-y-2">
                {REPORT_TYPES.map((rt) => (
                  <button
                    key={rt.value}
                    onClick={() => setFormData((prev) => ({ ...prev, type: rt.value }))}
                    className={`w-full text-left p-2.5 rounded-lg border transition-all text-sm ${
                      formData.type === rt.value
                        ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                        : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200 hover:border-gray-600'
                    }`}
                  >
                    <div className="font-medium">{rt.label}</div>
                    <div className="text-xs opacity-70 mt-0.5">{rt.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Export Format</label>
                <div className="flex gap-2">
                  {FORMAT_OPTIONS.map((fo) => (
                    <button
                      key={fo.value}
                      onClick={() => setFormData((prev) => ({ ...prev, format: fo.value }))}
                      className={`flex-1 flex items-center justify-center gap-2 p-2.5 rounded-lg border transition-all text-sm ${
                        formData.format === fo.value
                          ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                          : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={fo.icon} />
                      </svg>
                      {fo.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Entity IDs (optional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={entityIdInput}
                    onChange={(e) => setEntityIdInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddEntityId()}
                    placeholder="Add entity ID..."
                    className="flex-1 bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                  />
                  <button
                    onClick={handleAddEntityId}
                    className="px-3 py-2 rounded-lg bg-surface-tertiary border border-border-subtle text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-all text-sm"
                  >
                    Add
                  </button>
                </div>
                {formData.entity_ids && formData.entity_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.entity_ids.map((eid) => (
                      <span
                        key={eid}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-tertiary border border-border-subtle text-xs text-gray-300"
                      >
                        {eid}
                        <button onClick={() => handleRemoveEntityId(eid)} className="text-gray-500 hover:text-red-400">
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleGenerate}
                disabled={generateReport.isPending || !formData.title.trim()}
                className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generateReport.isPending ? 'Generating...' : 'Generate Report'}
              </button>

              {generateReport.isError && (
                <p className="text-xs text-red-400">Error: {(generateReport.error as Error).message}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Reports List */}
        <div className="xl:col-span-2">
          <div className="bg-surface-secondary rounded-xl border border-border-subtle overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle bg-surface-tertiary/50">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Title</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Format</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Size</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Created</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportsData?.items.map((report) => (
                    <tr
                      key={report.id}
                      onClick={() => setSelectedReportId(report.id)}
                      className={`border-b border-border-subtle/50 cursor-pointer transition-colors ${
                        selectedReportId === report.id ? 'bg-helios-700/20' : 'hover:bg-surface-tertiary/50'
                      }`}
                    >
                      <td className="py-3 px-4 text-gray-200 font-medium">{report.title}</td>
                      <td className="py-3 px-4 text-gray-400 capitalize">{report.type.replace('_', ' ')}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[report.status] || ''}`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 uppercase">{report.format}</td>
                      <td className="py-3 px-4 text-gray-400">{formatFileSize(report.file_size)}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{new Date(report.created_at).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {report.status === 'completed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadReport.mutate({ id: report.id, format: report.format });
                              }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-cyber-cyan hover:bg-surface-tertiary transition-colors"
                              title="Download"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('Delete this report?')) deleteReport.mutate(report.id);
                            }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-surface-tertiary transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!reportsData || reportsData.items.length === 0) && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500">
                        No reports yet. Click "New Report" to generate one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="xl:col-span-1">
          <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5 sticky top-6">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              Preview
            </h2>
            {selectedReport ? (
              <div className="space-y-4">
                <div>
                  <div className="text-lg font-semibold text-gray-100">{selectedReport.title}</div>
                  <div className="text-xs text-gray-500 mt-1 capitalize">{selectedReport.type.replace('_', ' ')}</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[selectedReport.status] || ''}`}>
                      {selectedReport.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Format</span>
                    <span className="text-gray-300 uppercase">{selectedReport.format}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Size</span>
                    <span className="text-gray-300">{formatFileSize(selectedReport.file_size)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Author</span>
                    <span className="text-gray-300">{selectedReport.author}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-300 text-xs">{new Date(selectedReport.created_at).toLocaleString()}</span>
                  </div>
                  {selectedReport.entity_ids.length > 0 && (
                    <div>
                      <span className="text-gray-500 block mb-1">Entities</span>
                      <div className="flex flex-wrap gap-1">
                        {selectedReport.entity_ids.map((eid) => (
                          <span key={eid} className="text-xs px-2 py-0.5 rounded bg-surface-tertiary border border-border-subtle text-gray-400">
                            {eid}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {selectedReport.content && (
                  <div className="mt-4 p-3 bg-surface-tertiary rounded-lg border border-border-subtle max-h-60 overflow-y-auto">
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">{selectedReport.content}</pre>
                  </div>
                )}

                {selectedReport.status === 'completed' && (
                  <div className="flex gap-2 pt-2">
                    {(['pdf', 'json', 'csv'] as ExportFormat[]).map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => downloadReport.mutate({ id: selectedReport.id, format: fmt })}
                        className="flex-1 px-3 py-2 rounded-lg bg-surface-tertiary border border-border-subtle text-gray-300 hover:text-cyber-cyan hover:border-cyber-cyan/30 transition-all text-xs font-medium uppercase"
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center">
                <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-sm text-gray-500">Select a report to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
